<template>
    <div class="kpi_card" :class="{ 'is-flat': flat }">
        <!-- Header: estático cuando la card es standalone; toggle (botón) cuando
            es collapsible. El chevron + animación replican el lenguaje de
            CollapsibleSection para que el colapso se sienta nativo del feature. -->
        <component :is="collapsible ? 'button' : 'div'"
            class="card_header is-flex is-align-items-center mt-3"
            :class="{ card_toggle: collapsible }"
            :type="collapsible ? 'button' : null"
            :aria-expanded="collapsible ? String(expanded) : null"
            :aria-controls="collapsible ? tableId : null"
            @click="onHeaderClick">
            <PieChartIcon class="header_icon" />
            <span class="card_title">Performance de clientes</span>
            <svg v-if="collapsible" class="chevron" :class="{ 'is-collapsed': !expanded }"
                viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </component>

        <transition name="kpi_collapse"
            @enter="onEnter"
            @after-enter="onAfterEnter"
            @before-leave="onBeforeLeave"
            @leave="onLeave">
        <div v-show="!collapsible || expanded" :id="tableId" class="kpi_table_wrap">
        <table class="kpi_table">
            <thead>
                <tr>
                    <th class="metric_col">Métrica</th>
                    <th class="value_col has-text-right">Valor</th>
                    <th class="pct_col has-text-right">MoM</th>
                    <th class="pct_col has-text-right">YoY</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="kpi in kpis" :key="kpi.metric">
                    <th scope="row" class="metric_cell">
                        <svg v-if="metricIcon(kpi.metric)"
                            class="metric_icon" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round"
                            aria-hidden="true">
                            <path :d="metricIcon(kpi.metric)" />
                        </svg>
                        <span class="metric_label">{{ kpi.metric }}</span>
                    </th>
                    <td class="value_cell has-text-right">{{ formatValue(kpi) }}</td>
                    <td class="pct_cell has-text-right" :class="pctClass(kpi.mom)">
                        {{ formatPct(kpi.mom) }}
                    </td>
                    <td class="pct_cell has-text-right" :class="pctClass(kpi.yoy)">
                        {{ formatPct(kpi.yoy) }}
                    </td>
                </tr>
            </tbody>
        </table>
        </div>
        </transition>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";

// Formateador de números enteros (es-CL) module-scoped — `Intl.NumberFormat`
// es caro de instanciar y no depende de estado del componente.
const COUNT_FORMATTER = new Intl.NumberFormat("es-CL");

// Contador module-scoped para IDs estables del aria-controls por instancia
// (mismo patrón que CollapsibleSection — no usa `this._uid`, removido en Vue 3).
let _instanceCounter = 0;

// Mapa metric → SVG path. Single-path por entrada (concatenamos subpaths con
// múltiples comandos M dentro del mismo d). Si la lista de métricas crece o
// pasa a ser data-driven desde el backend, conviene extraer estos a
// componentes propios en /components/svg/ — por ahora el set es chico y fijo.
const METRIC_ICON_PATHS = {
    // Tarjeta con línea divisoria (credit-card simplificado).
    "Ticket Promedio": "M3 7h18v10H3zM3 11h18",
    // Casita / storefront.
    "Cantidad de clientes": "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10",
    // Refresh-cw (dos flechas circulares).
    "Clientes recurrentes": "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
};

export default {
    name: "ClientsKpiCard",
    components: { PieChartIcon },
    props: {
        kpis: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((k) => typeof k.metric === "string"),
        },
        // Modo embebido: sin chrome propio (borde/sombra/fondo/padding) para
        // vivir dentro de otra card (HeadlineMonthCard la apila bajo el monto).
        // Standalone (default) conserva su recuadro.
        flat: { type: Boolean, default: false },
        // Opt-in: el header pasa a ser un botón que colapsa/expande la tabla.
        // Sin esto la card se comporta igual que siempre (tabla siempre visible).
        collapsible: { type: Boolean, default: false },
        // Estado inicial cuando es collapsible. El drawer de Ventas la abre
        // colapsada: la performance es detalle secundario frente al monto y el
        // ranking. No se persiste — arranca colapsada en cada apertura del modal.
        defaultExpanded: { type: Boolean, default: true },
    },
    data() {
        return {
            expanded: this.defaultExpanded,
            tableId: `kpi-table-${++_instanceCounter}`,
        };
    },
    methods: {
        onHeaderClick() {
            if (this.collapsible) this.toggle();
        },
        toggle() {
            this.expanded = !this.expanded;
            // `change` (no `update:expanded`): la prop `expanded` no existe, la
            // card es uncontrolled. Mismo contrato que CollapsibleSection.
            this.$emit("change", this.expanded);
        },

        // Hooks JS de <transition>: animan max-height entre 0 y scrollHeight
        // (CSS no anima `auto`). Reseteamos los estilos inline al final para no
        // dejar el max-height fijo. Replicado de CollapsibleSection.
        onEnter(el) {
            el.style.maxHeight = "0";
            el.style.opacity = "0";
            el.style.overflow = "hidden";
            void el.offsetHeight;
            el.style.transition =
                "max-height 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out";
            el.style.maxHeight = `${el.scrollHeight}px`;
            el.style.opacity = "1";
        },
        onAfterEnter(el) {
            el.style.maxHeight = "";
            el.style.opacity = "";
            el.style.overflow = "";
            el.style.transition = "";
        },
        onBeforeLeave(el) {
            el.style.maxHeight = `${el.scrollHeight}px`;
            el.style.opacity = "1";
            el.style.overflow = "hidden";
        },
        onLeave(el) {
            void el.offsetHeight;
            el.style.transition =
                "max-height 240ms cubic-bezier(0.4, 0, 1, 1), opacity 160ms ease-in";
            el.style.maxHeight = "0";
            el.style.opacity = "0";
        },
        formatValue(kpi) {
            if (kpi.format === "currency") return parseToMoney(kpi.value);
            // "count" (default) — entero con separador de miles es-CL.
            return COUNT_FORMATTER.format(kpi.value);
        },
        formatPct(pct) {
            // ↑/↓ + valor, sin + en positivos (color verde carga la dirección).
            // Negativos sí muestran "-" — color solo no es suficiente para
            // accesibilidad.
            return formatVariation(pct, { withArrow: true, arrowStyle: "line" });
        },
        pctClass(pct) {
            if (pct == null) return "pct_cell--neutral";
            if (pct > 0) return "pct_cell--positive";
            if (pct < 0) return "pct_cell--negative";
            return "pct_cell--neutral";
        },
        metricIcon(metric) {
            return METRIC_ICON_PATHS[metric] ?? null;
        },
    },
};
</script>

<style scoped lang="scss">
.kpi_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: var(--report-shadow-card);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    // Embebido en otra card: se deshace del recuadro propio y deja que el
    // contenido fluya con la altura natural (la card padre es el clamp).
    &.is-flat {
        background: transparent;
        border: none;
        border-radius: 0;
        box-shadow: none;
        padding: 0;
        height: auto;
        overflow: visible;
    }
}

.card_header {
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

// Variante toggle: el header es un botón full-width. Reset del chrome nativo +
// hover sutil sobre --report-surface, igual que el toggle de CollapsibleSection.
.card_toggle {
    width: 100%;
    background: transparent;
    border: none;
    border-radius: 8px;
    margin-left: -6px;
    padding: 4px 6px;
    color: var(--report-text);
    text-align: left;
    cursor: pointer;
    transition: background-color 160ms ease;

    &:hover {
        background: var(--report-surface);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
    }
}

// Chevron del toggle — mismo glyph, tamaño y easing que CollapsibleSection.
.chevron {
    width: 14px;
    height: 14px;
    margin-right: 6px;
    flex-shrink: 0;
    color: var(--report-text-muted);
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);

    &.is-collapsed {
        transform: rotate(-90deg);
    }
}

// Defensive: en Stage 1 son 3 filas; el wrap permite que la card respete su
// altura aunque Stage 2 sume más métricas.
.kpi_table_wrap {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.kpi_table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    th,
    td {
        padding: 10px 8px;
        font-size: 14px;
        line-height: 20px;
    }

    thead th {
        color: var(--report-text-muted);
        font-size: 12px;
        font-weight: 600;
        border-bottom: 1px solid var(--report-divider-soft);
    }

    // Sólo font-weight acá — el color de cada celda lo define su clase
    // específica (.metric_label, .value_cell, .pct_cell--*). Si seteamos un
    // color genérico en `tbody td`, su especificidad (.kpi_table tbody td)
    // supera a `.pct_cell--positive` y las flechas pierden el verde/rojo
    // semántico.
    tbody th,
    tbody td {
        font-weight: 500;
    }

    tbody tr + tr th,
    tbody tr + tr td {
        border-top: 1px solid var(--report-divider-soft);
    }

    .metric_col { width: 44%; }
    .value_col  { width: 24%; }
    .pct_col    { width: 16%; }

    // Números alineados por columna: tabular-nums evita que los dígitos de
    // ancho variable desalineen valores y porcentajes entre filas.
    .value_cell,
    .pct_cell {
        font-variant-numeric: tabular-nums;
    }

    // Flecha + porcentaje en una sola línea — la columna es angosta y sin esto
    // el "↑" se parte a un renglón propio.
    .pct_cell {
        white-space: nowrap;
    }
}

.metric_cell {
    text-align: left;
    display: flex;
    align-items: center;
    gap: 10px;

    .metric_icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        color: var(--report-text-muted);
    }

    .metric_label {
        color: var(--report-text);
    }
}

.value_cell {
    color: var(--report-text);
    font-weight: 600;
}

.pct_cell--positive { color: var(--report-positive); }
.pct_cell--negative { color: var(--report-negative); }
.pct_cell--neutral  { color: var(--report-text-muted); }
</style>
